import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Descriptions,
  Divider,
  List,
  Modal,
  Select,
  Skeleton,
  Space,
  Tag,
  Typography,
} from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  CloudUploadOutlined,
  ExclamationCircleOutlined,
  ReloadOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

import {
  certificationSyncService,
  type PushErrorBody,
} from "../../../services/api/certificationSyncService";
import type {
  CertificationSyncReport,
  RemoteStatus,
} from "../../../types/certification-sync";

interface Props {
  open: boolean;
  producerId: number | null;
  producerName?: string;
  onClose: () => void;
}

/**
 * Map raw cert validation errors to a user-actionable hint i18n key.
 * Returns the i18n key (lookup happens in the renderer so the key can be
 * passed to `t(...)` with interpolation) or null when no hint applies.
 */
const errorHintKey = (errors: Record<string, string> | undefined): string | null => {
  if (!errors) return null;
  const entries = Object.entries(errors);
  if (entries.some(([f, msg]) => f === "farm.database_id" && /already used/i.test(msg))) {
    return "certification_sync.hint_farm_id_taken";
  }
  if (entries.some(([f, msg]) => f === "producer.database_id" && /already used/i.test(msg))) {
    return "certification_sync.hint_producer_id_taken";
  }
  if (entries.some(([f, msg]) => f.endsWith("town_name") && /ambiguous/i.test(msg))) {
    return "certification_sync.hint_town_ambiguous";
  }
  if (entries.some(([f, msg]) => f.endsWith("town_name") && /not found/i.test(msg))) {
    return "certification_sync.hint_town_not_found";
  }
  if (entries.some(([f]) => f === "farm.geom" || f === "geom")) {
    return "certification_sync.hint_geom_invalid";
  }
  return null;
};

const formatDate = (raw: string | null | undefined): string =>
  raw ? dayjs(raw).format("DD/MM/YYYY HH:mm") : "—";

const remoteStatusTag = (status: RemoteStatus, t: (k: string) => string) => {
  switch (status) {
    case "found":
      return (
        <Tag color="green" icon={<CheckCircleOutlined />}>
          {t("certification_sync.remote_status_found")}
        </Tag>
      );
    case "not_found":
      return (
        <Tag color="orange" icon={<ExclamationCircleOutlined />}>
          {t("certification_sync.remote_status_not_found")}
        </Tag>
      );
    case "disabled":
      return (
        <Tag color="default" icon={<CloseCircleOutlined />}>
          {t("certification_sync.remote_status_disabled")}
        </Tag>
      );
    case "auth_error":
    case "error":
      return (
        <Tag color="red" icon={<CloseCircleOutlined />}>
          {t(`certification_sync.remote_status_${status}`)}
        </Tag>
      );
  }
};

export const CertificationSyncModal = ({ open, producerId, producerName, onClose }: Props) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState<CertificationSyncReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedFarmIds, setSelectedFarmIds] = useState<number[]>([]);
  const [pushing, setPushing] = useState(false);
  const [pushError, setPushError] = useState<PushErrorBody | null>(null);
  const [updating, setUpdating] = useState(false);
  const [updateError, setUpdateError] = useState<PushErrorBody | null>(null);
  const [updatedFields, setUpdatedFields] = useState<string[] | null>(null);
  const [farmUpdatingId, setFarmUpdatingId] = useState<number | null>(null);
  const [farmFeedback, setFarmFeedback] = useState<{
    farmId: number;
    success?: { kind: "created" | "updated"; updated: string[] };
    error?: PushErrorBody;
  } | null>(null);

  const fetchReport = async () => {
    if (!producerId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await certificationSyncService.getProducerStatus(producerId);
      setReport(data);
      // Default to selecting ALL active farms so the create button works
      // on first click. The user can still deselect specific ones.
      if (data.local.farms.length > 0) {
        setSelectedFarmIds((prev) =>
          prev.length > 0 ? prev : data.local.farms.map((f) => f.id),
        );
      }
    } catch (e: any) {
      setError(e?.response?.data?.detail || t("certification_sync.fetch_error"));
    } finally {
      setLoading(false);
    }
  };

  // The apiClient interceptor wraps non-2xx responses as
  // `{ message, status, originalError }` and discards `response.data`. To
  // recover the structured body (kind / detail / errors) we look at
  // `originalError.response.data`; we keep `response.data` as a fallback
  // in case the interceptor changes.
  const extractErrorBody = (e: any): PushErrorBody | undefined => {
    const body =
      (e?.originalError?.response?.data as PushErrorBody | undefined) ??
      (e?.response?.data as PushErrorBody | undefined);
    if (body && (body.detail || body.errors || body.kind)) return body;
    // Last fallback: use the interceptor-provided message string.
    if (e?.message) return { detail: String(e.message) };
    return undefined;
  };

  const handlePush = async () => {
    if (!producerId || selectedFarmIds.length === 0) return;
    setPushing(true);
    setPushError(null);
    try {
      const res = await certificationSyncService.pushProducer(producerId, selectedFarmIds);
      setReport(res.report);
    } catch (e: any) {
      setPushError(
        extractErrorBody(e) || { detail: t("certification_sync.push_error_generic") },
      );
    } finally {
      setPushing(false);
    }
  };

  const handleUpdate = async () => {
    if (!producerId) return;
    setUpdating(true);
    setUpdateError(null);
    setUpdatedFields(null);
    try {
      const res = await certificationSyncService.updateProducer(producerId);
      setReport(res.report);
      const remote = res.remote as { updated_fields?: string[] } | null;
      setUpdatedFields(remote?.updated_fields ?? []);
    } catch (e: any) {
      setUpdateError(
        extractErrorBody(e) || { detail: t("certification_sync.update_error_generic") },
      );
    } finally {
      setUpdating(false);
    }
  };

  const handleFarmUpdate = async (farmId: number) => {
    setFarmUpdatingId(farmId);
    setFarmFeedback(null);
    try {
      const res = await certificationSyncService.updateFarm(farmId);
      setReport(res.report);
      const remote = res.remote as { updated_fields?: string[] } | null;
      setFarmFeedback({ farmId, success: { kind: "updated", updated: remote?.updated_fields ?? [] } });
    } catch (e: any) {
      setFarmFeedback({
        farmId,
        error:
          extractErrorBody(e) ||
          { detail: t("certification_sync.farm_update_error_generic") },
      });
    } finally {
      setFarmUpdatingId(null);
    }
  };

  const handleFarmCreate = async (farmId: number) => {
    setFarmUpdatingId(farmId);
    setFarmFeedback(null);
    try {
      const res = await certificationSyncService.createFarm(farmId);
      setReport(res.report);
      setFarmFeedback({ farmId, success: { kind: "created", updated: [] } });
    } catch (e: any) {
      setFarmFeedback({
        farmId,
        error:
          extractErrorBody(e) ||
          { detail: t("certification_sync.farm_create_error_generic") },
      });
    } finally {
      setFarmUpdatingId(null);
    }
  };

  useEffect(() => {
    if (open && producerId) {
      void fetchReport();
    }
    if (!open) {
      setReport(null);
      setError(null);
      setPushError(null);
      setSelectedFarmIds([]);
      setUpdateError(null);
      setUpdatedFields(null);
      setFarmUpdatingId(null);
      setFarmFeedback(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, producerId]);

  const farmOptions = useMemo(() => {
    if (!report) return [];
    return report.local.farms.map((f) => ({ value: f.id, label: f.name || `#${f.id}` }));
  }, [report]);

  const localPendingFarms = report?.local.farms_needing_push ?? [];
  const farmsById = new Map(
    (report?.local.farms ?? []).map((f) => [f.id, f]),
  );

  return (
    <Modal
      open={open}
      title={
        <Space>
          <SyncOutlined />
          {t("certification_sync.title")}
          {producerName && <Typography.Text type="secondary">— {producerName}</Typography.Text>}
        </Space>
      }
      onCancel={onClose}
      width={720}
      footer={[
        <Button
          key="reload"
          icon={<ReloadOutlined />}
          onClick={fetchReport}
          loading={loading}
        >
          {t("certification_sync.refresh")}
        </Button>,
        <Button key="close" type="primary" onClick={onClose}>
          {t("common.close", "Cerrar")}
        </Button>,
      ]}
    >
      {loading && <Skeleton active />}

      {error && !loading && (
        <Alert type="error" showIcon message={error} style={{ marginBottom: 12 }} />
      )}

      {!loading && !error && report && (
        <>
          {/* Local pending push block */}
          <Typography.Title level={5} style={{ marginTop: 0 }}>
            {t("certification_sync.pending_local_section")}
          </Typography.Title>
          {report.local.needs_push || localPendingFarms.length > 0 ? (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              message={
                <>
                  {report.local.needs_push && (
                    <div>
                      {report.local.certification_synced_at
                        ? t("certification_sync.producer_edited_since_sync")
                        : t("certification_sync.producer_never_synced")}
                    </div>
                  )}
                  {localPendingFarms.length > 0 && (
                    <div>
                      {t("certification_sync.farms_pending_count", { count: localPendingFarms.length })}:{" "}
                      {localPendingFarms
                        .map((id) => farmsById.get(id)?.name || `#${id}`)
                        .join(", ")}
                    </div>
                  )}
                </>
              }
            />
          ) : (
            <Alert
              type="success"
              showIcon
              style={{ marginBottom: 16 }}
              message={t("certification_sync.nothing_pending_local")}
            />
          )}

          <Descriptions size="small" column={1} bordered style={{ marginBottom: 24 }}>
            <Descriptions.Item label={t("certification_sync.last_local_modified")}>
              {formatDate(report.local.modified)}
            </Descriptions.Item>
            <Descriptions.Item label={t("certification_sync.last_sync_at")}>
              {formatDate(report.local.certification_synced_at)}
            </Descriptions.Item>
            <Descriptions.Item label={t("certification_sync.local_farms_count")}>
              {report.local.farms.length}
            </Descriptions.Item>
          </Descriptions>

          {/* Remote status block */}
          <Typography.Title level={5}>
            {t("certification_sync.remote_section")}
          </Typography.Title>

          <div style={{ marginBottom: 12 }}>
            {remoteStatusTag(report.remote_status, t)}
            {report.error && (
              <Typography.Text type="secondary" style={{ marginLeft: 8 }}>
                {report.error}
              </Typography.Text>
            )}
          </div>

          {report.remote_status === "found" && report.remote && (
            <>
              <Descriptions size="small" column={1} bordered>
                <Descriptions.Item label={t("certification_sync.remote_external_id")}>
                  <Typography.Text code>{report.remote.producer.external_id}</Typography.Text>
                </Descriptions.Item>
                <Descriptions.Item label={t("certification_sync.remote_username")}>
                  {report.remote.producer.username || "—"}
                </Descriptions.Item>
                <Descriptions.Item label={t("certification_sync.remote_name")}>
                  {report.remote.producer.name}
                </Descriptions.Item>
                <Descriptions.Item label={t("certification_sync.remote_portfolio")}>
                  {report.remote.producer.portfolio_label || "—"}
                </Descriptions.Item>
                <Descriptions.Item label={t("certification_sync.remote_farms_count")}>
                  {report.remote.farm_ids.length}
                </Descriptions.Item>
              </Descriptions>

              {(report.missing_in_remote?.length ?? 0) > 0 && (
                <Alert
                  type="warning"
                  showIcon
                  style={{ marginTop: 12 }}
                  message={t("certification_sync.missing_in_remote_title")}
                  description={
                    <span>
                      {report.missing_in_remote!
                        .map((id) => farmsById.get(id)?.name || `#${id}`)
                        .join(", ")}
                    </span>
                  }
                />
              )}

              {(report.extra_in_remote?.length ?? 0) > 0 && (
                <Alert
                  type="warning"
                  showIcon
                  style={{ marginTop: 12 }}
                  message={t("certification_sync.extra_in_remote_title")}
                  description={
                    <>
                      <div>{report.extra_in_remote!.join(", ")}</div>
                      <div style={{ marginTop: 4 }}>
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                          {t("certification_sync.extra_in_remote_help")}
                        </Typography.Text>
                      </div>
                    </>
                  }
                />
              )}

              <div style={{ marginTop: 16, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                <Button
                  type="primary"
                  icon={<CloudUploadOutlined />}
                  loading={updating}
                  onClick={handleUpdate}
                >
                  {t("certification_sync.update_button")}
                </Button>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {t("certification_sync.update_help")}
                </Typography.Text>
              </div>

              {updatedFields !== null && !updateError && (
                <Alert
                  type={updatedFields.length > 0 ? "success" : "info"}
                  showIcon
                  style={{ marginTop: 12 }}
                  message={
                    updatedFields.length > 0
                      ? t("certification_sync.update_success", { fields: updatedFields.join(", ") })
                      : t("certification_sync.update_noop")
                  }
                />
              )}

              {updateError && (
                <Alert
                  type="error"
                  showIcon
                  style={{ marginTop: 12 }}
                  message={updateError.detail || t("certification_sync.update_error_generic")}
                  description={
                    <>
                      {updateError.errors && Object.keys(updateError.errors).length > 0 && (
                        <ul style={{ marginBottom: 8, paddingLeft: 16 }}>
                          {Object.entries(updateError.errors).map(([field, msg]) => (
                            <li key={field}>
                              <Typography.Text code>{field}</Typography.Text>: {msg}
                            </li>
                          ))}
                        </ul>
                      )}
                      {(() => {
                        const hint = errorHintKey(updateError.errors);
                        if (!hint) return null;
                        return (
                          <Typography.Text type="secondary" style={{ display: "block", fontSize: 13 }}>
                            <strong>{t("certification_sync.hint_label")}:</strong>{" "}
                            {t(hint)}
                          </Typography.Text>
                        );
                      })()}
                    </>
                  }
                />
              )}

              {report.local.farms.length > 0 && (
                <>
                  <Divider style={{ margin: "20px 0 12px" }} />
                  <Typography.Text strong>
                    {t("certification_sync.farms_section")}
                  </Typography.Text>
                  <List
                    size="small"
                    style={{ marginTop: 8 }}
                    dataSource={report.local.farms}
                    rowKey="id"
                    renderItem={(farm) => {
                      const existsRemote = report.remote!.farm_ids.includes(farm.id);
                      // A farm with sync history is known to exist somewhere
                      // in certification — even if it's not in THIS producer's
                      // remote farm list, the PATCH endpoint can pull it over
                      // (reassignment).
                      const wasEverSynced = farm.certification_synced_at != null;
                      const isMisassigned = !existsRemote && wasEverSynced;
                      const localPending =
                        farm.needs_push ||
                        (farm.certification_synced_at == null);
                      const feedback =
                        farmFeedback?.farmId === farm.id ? farmFeedback : null;
                      const showUpdateButton = existsRemote || isMisassigned;
                      return (
                        <List.Item
                          actions={[
                            showUpdateButton ? (
                              <Button
                                key="update"
                                size="small"
                                type="primary"
                                icon={<CloudUploadOutlined />}
                                loading={farmUpdatingId === farm.id}
                                onClick={() => handleFarmUpdate(farm.id)}
                              >
                                {t(
                                  isMisassigned
                                    ? "certification_sync.farm_reassign_button"
                                    : "certification_sync.farm_update_button",
                                )}
                              </Button>
                            ) : (
                              <Button
                                key="create"
                                size="small"
                                icon={<CloudUploadOutlined />}
                                loading={farmUpdatingId === farm.id}
                                onClick={() => handleFarmCreate(farm.id)}
                              >
                                {t("certification_sync.farm_create_button")}
                              </Button>
                            ),
                          ]}
                        >
                          <Space size={8} wrap>
                            <Typography.Text strong>
                              {farm.name || `#${farm.id}`}
                            </Typography.Text>
                            {isMisassigned && (
                              <Tag color="purple">
                                {t("certification_sync.farm_status_misassigned")}
                              </Tag>
                            )}
                            {!existsRemote && !isMisassigned && (
                              <Tag color="orange">
                                {t("certification_sync.farm_status_only_local")}
                              </Tag>
                            )}
                            {existsRemote && localPending && (
                              <Tag color="gold">
                                {t("certification_sync.farm_status_local_changes")}
                              </Tag>
                            )}
                            {existsRemote && !localPending && (
                              <Tag color="green">
                                {t("certification_sync.farm_status_in_sync")}
                              </Tag>
                            )}
                            {feedback?.success && (
                              <Typography.Text type="success" style={{ fontSize: 12 }}>
                                {feedback.success.kind === "created"
                                  ? t("certification_sync.farm_create_success")
                                  : feedback.success.updated.length > 0
                                    ? t("certification_sync.farm_update_success", {
                                        fields: feedback.success.updated.join(", "),
                                      })
                                    : t("certification_sync.farm_update_noop")}
                              </Typography.Text>
                            )}
                            {feedback?.error && (
                              <Typography.Text type="danger" style={{ fontSize: 12 }}>
                                {feedback.error.detail ||
                                  t("certification_sync.farm_update_error_generic")}
                              </Typography.Text>
                            )}
                          </Space>
                        </List.Item>
                      );
                    }}
                  />
                  {farmFeedback?.error?.errors &&
                    Object.keys(farmFeedback.error.errors).length > 0 && (
                      <Alert
                        type="error"
                        showIcon
                        style={{ marginTop: 8 }}
                        message={farmFeedback.error.detail ||
                          t("certification_sync.farm_update_error_generic")}
                        description={
                          <>
                            <ul style={{ marginBottom: 8, paddingLeft: 16 }}>
                              {Object.entries(farmFeedback.error.errors).map(([field, msg]) => (
                                <li key={field}>
                                  <Typography.Text code>{field}</Typography.Text>: {msg}
                                </li>
                              ))}
                            </ul>
                            {(() => {
                              const hint = errorHintKey(farmFeedback!.error!.errors);
                              if (!hint) return null;
                              return (
                                <Typography.Text type="secondary" style={{ display: "block", fontSize: 13 }}>
                                  <strong>{t("certification_sync.hint_label")}:</strong>{" "}
                                  {t(hint)}
                                </Typography.Text>
                              );
                            })()}
                          </>
                        }
                      />
                    )}
                </>
              )}
            </>
          )}

          {report.remote_status === "not_found" && (
            <>
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 12 }}
                message={t("certification_sync.remote_not_found_help")}
              />

              {farmOptions.length === 0 ? (
                <Alert
                  type="info"
                  showIcon
                  message={t("certification_sync.no_farms_to_push")}
                />
              ) : (
                <>
                  <Typography.Text strong>
                    {t("certification_sync.choose_farms_to_push")}
                  </Typography.Text>
                  <div style={{ marginTop: 8, display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <Select
                      mode="multiple"
                      style={{ minWidth: 280, flex: 1 }}
                      value={selectedFarmIds}
                      onChange={(v) => setSelectedFarmIds(v)}
                      options={farmOptions}
                      showSearch
                      optionFilterProp="label"
                      disabled={pushing}
                      placeholder={t("certification_sync.choose_farms_placeholder")}
                      maxTagCount="responsive"
                      getPopupContainer={(trigger) =>
                        trigger.closest(".ant-modal-body") as HTMLElement || document.body
                      }
                    />
                    <Button
                      type="primary"
                      icon={<CloudUploadOutlined />}
                      loading={pushing}
                      disabled={selectedFarmIds.length === 0}
                      onClick={handlePush}
                    >
                      {t("certification_sync.push_button", {
                        count: selectedFarmIds.length,
                      })}
                    </Button>
                  </div>
                  <Typography.Text type="secondary" style={{ display: "block", marginTop: 6, fontSize: 12 }}>
                    {t("certification_sync.push_multi_help")}
                  </Typography.Text>
                </>
              )}

              {pushError && (
                <Alert
                  type="error"
                  showIcon
                  style={{ marginTop: 12 }}
                  message={
                    pushError.detail ||
                    (pushError.kind === "payload_incomplete"
                      ? t("certification_sync.push_error_payload")
                      : t("certification_sync.push_error_generic"))
                  }
                  description={
                    <>
                      {pushError.errors && Object.keys(pushError.errors).length > 0 && (
                        <ul style={{ marginBottom: 8, paddingLeft: 16 }}>
                          {Object.entries(pushError.errors).map(([field, msg]) => (
                            <li key={field}>
                              <Typography.Text code>{field}</Typography.Text>: {msg}
                            </li>
                          ))}
                        </ul>
                      )}
                      {(() => {
                        const hint = errorHintKey(pushError.errors);
                        if (!hint) return null;
                        return (
                          <Typography.Text type="secondary" style={{ display: "block", fontSize: 13 }}>
                            <strong>{t("certification_sync.hint_label")}:</strong>{" "}
                            {t(hint)}
                          </Typography.Text>
                        );
                      })()}
                    </>
                  }
                />
              )}
            </>
          )}
        </>
      )}
    </Modal>
  );
};

export default CertificationSyncModal;
